import { AnimatePresence, motion } from 'framer-motion';
import { Color, InteractionConfig } from './types';
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

function rgbaFrom(c: Color | undefined): string | undefined {
	if (!c) return undefined;
	return `rgba(${c.Red}, ${c.Green}, ${c.Blue}, ${c.Alpha})`;
}

export default function Interaction(props: {
	shouldPlay: boolean;
	config: InteractionConfig;
	onLoaded: () => void;
	onDone: () => void;
	onError: (e: Error) => void;
}) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [showAudio, setShowBanner] = useState(true);
	const [retries, setRetries] = useState(0);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			props.onError(new Error('did not find audio player element'));
			return;
		}

		if (props.config.settings?.Volume && props.config.settings?.Volume > 1) {
			audio.volume = props.config.settings.Volume / 100;
		} else {
			audio.volume = props.config.settings?.Volume || 0.7;
		}

		if (props.shouldPlay) {
			audio.play().catch((e) => {
				console.error('error playing audio', e);
				props.onError(e);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.shouldPlay, retries]);

	const handleEnded = useCallback(() => {
		setShowBanner(false);
		setTimeout(() => {
			props.onDone();
		}, 500);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.onDone]);

	const handleCanPlay = useCallback((e: SyntheticEvent<HTMLAudioElement>) => {
		const audio = e.currentTarget as HTMLAudioElement;
		console.log(`Audio duration: ${audio.duration} seconds`);
		props.onLoaded();
		// ensure even long audio files are not cut off from the overlay
		const durationWithBuffer = audio.duration + 10;
		window.parent.postMessage({ status: 'duration_update', duration: durationWithBuffer }, '*');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.onLoaded]);

	const handleError = useCallback((e: SyntheticEvent<HTMLAudioElement>) => {
		const audio = e.currentTarget as HTMLAudioElement;
		console.error(`error in audio player`, audio.error);
		if (retries < 3) {
			setRetries(retries + 1);
			console.log(`retrying audio, attempt ${retries}`);
		}
	}, [retries]);

	return (
		<div className="min-w-[740px] min-h-[130px]">
			<audio
				ref={audioRef}
				id="player"
				autoPlay={false}
				src={props.config.audioSrc + (retries > 0 ? '?retry=' + retries : '')}
				onEnded={handleEnded}
				onCanPlay={handleCanPlay}
				onError={handleError}
			/>
			<AnimatePresence>{showAudio && <SoundBiteDisplay {...props.config} />}</AnimatePresence>
		</div>
	);
}

export const SoundBiteDisplay = (props: InteractionConfig) => {
	return (
		<motion.div
			className="rounded-[12px] flex justify-center items-center w-full h-full border-2 relative  overflow-hidden"
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: 10 }}
			transition={{ duration: 0.5, type: 'spring' }}
			style={{
				backgroundColor: rgbaFrom(props.overlaySettings?.Popup?.BackgroundColor),
				borderColor: rgbaFrom(props.overlaySettings?.Popup?.BorderColor),
				color: rgbaFrom(props.overlaySettings?.Popup?.FontColor)
			}}
		>
			<div
				className="rounded-[10px] flex justify-start items-center align-middle text-white p-3 pr-4 w-[99.5%] h-[97%] gap-4 z-10"
				style={{
					borderColor: rgbaFrom(props.overlaySettings?.Popup?.BorderColor),
					color: rgbaFrom(props.overlaySettings?.Popup?.FontColor)
				}}
			>
				<div
					className="rounded-full relative w-[104px] h-[104px] shrink-0 grow-0 flex justify-center items-center align-middle"
					style={{
						backgroundColor: rgbaFrom(props.overlaySettings?.Popup?.BorderColor)
					}}>
					<div
						className="rounded-full relative h-[100px] w-[100px]"
						style={{
							background: `url("${props.picSrc}")`,
							backgroundSize: 'cover',
							backgroundRepeat: 'no-repeat'
						}}
					/>
					<img className="absolute bottom-0 right-0 w-11" src={'/soundbite-icon.png'}  alt="icon"/>
				</div>
				<div className="flex flex-col justify-around py-3 text-left h-full items-start w-full shrink-1 z-20">
					<div className="flex gap-1 doint text-[22px]">
						{props.sender} played a Soundbite:
					</div>
					<p className="text-2xl text-[28px] font-medium font-base">"{props.soundBiteName.trim()}"</p>
				</div>
				{/* <PoweredBy /> */}
				<div className="absolute top-0 right-0 rounded-bl-xl border-2 border-r-0 border-t-0 p-[8px] bg-neutral-800 z-20"
						 style={{
							 borderColor: rgbaFrom(props.overlaySettings?.Popup?.BorderColor)
						 }}><img src="/tangia-logo.svg" height={24} width={24} alt="logo" /></div>
				<img src="/speaker.svg" width={200} height={200}
						 className="absolute right-[100px] top-[10px] z-10 opacity-70" alt="" />
				<img src="/speaker.svg" width={90} height={90} className="absolute right-[10px] top-[30px] z-10 opacity-70"
						 style={{
							 transform: 'scaleX(-1)'
						 }} alt="" />
			</div>
		</motion.div>
	);
};
